import React, { useState, useCallback } from 'react';
import { toast } from 'sonner';
import { adminApi } from '../lib/adminAuth';
import {
  Upload, FileSpreadsheet, AlertCircle, CheckCircle2, Loader2,
  AlertTriangle, X, FileText, TrendingUp, Users, Receipt, IndianRupee,
  ShieldAlert, History, ArrowRight, ChevronDown, ChevronUp,
} from 'lucide-react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from './ui/select';
import { Badge } from './ui/badge';
import { formatCurrency, formatNumber } from '../lib/formatters';

const fmt = (n) => formatCurrency(n);

// ──────────────────────────────────────────────────────────────────
// MAIN WIZARD COMPONENT
// ──────────────────────────────────────────────────────────────────
export default function ZohoReportImport({ open, onClose, onComplete }) {
  const [step, setStep] = useState('upload'); // upload | preview | importing | done
  const [loading, setLoading] = useState(false);
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [result, setResult] = useState(null);

  // Resolution state
  const [duplicateResolutions, setDuplicateResolutions] = useState({}); // invNo → skip|overwrite
  const [stateResolutions, setStateResolutions] = useState({}); // invNo → state
  const [showSection, setShowSection] = useState({ ready: false, needs_state: true, duplicates: true, invalid: true });

  const reset = useCallback(() => {
    setStep('upload'); setLoading(false); setFile(null); setPreview(null);
    setResult(null); setDuplicateResolutions({}); setStateResolutions({});
  }, []);

  const handleClose = () => { reset(); onClose(); };

  const onFileUpload = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const lower = f.name.toLowerCase();
    if (!lower.endsWith('.xlsx') && !lower.endsWith('.csv')) {
      toast.error('Please upload an XLSX or CSV file');
      return;
    }
    setFile(f);
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append('file', f);
      const res = await adminApi.post('/api/admin/accounting/import-zoho-report/preview', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setPreview(res.data);
      setStep('preview');
      toast.success(`Parsed ${res.data.summary.unique_invoices} invoices`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to parse file');
      setFile(null);
    } finally { setLoading(false); }
  };

  const canConfirm = () => {
    if (!preview) return false;
    const needsState = preview.needs_state || [];
    const unresolved = needsState.filter((r) => !stateResolutions[r.invoice_number]);
    return unresolved.length === 0;
  };

  const onConfirmImport = async () => {
    if (!preview?.preview_token) return;
    setLoading(true);
    setStep('importing');
    try {
      const res = await adminApi.post('/api/admin/accounting/import-zoho-report/confirm', {
        preview_token: preview.preview_token,
        duplicate_resolutions: duplicateResolutions,
        state_resolutions: stateResolutions,
        apply_retainer_suggestions: false, // Q10b: user chose no smart-guess
      });
      setResult(res.data);
      setStep('done');
      toast.success(res.data.message);
      if (onComplete) onComplete();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Import failed');
      setStep('preview');
    } finally { setLoading(false); }
  };

  // ────────────────── RENDER ──────────────────
  const s = preview?.summary || {};

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-5xl max-h-[90vh] overflow-hidden flex flex-col" data-testid="zoho-report-import-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileSpreadsheet className="w-5 h-5 text-emerald-600" />
            Import from Zoho (Invoice Details Report)
          </DialogTitle>
        </DialogHeader>

        {/* Step indicator */}
        <div className="flex items-center gap-2 px-1 py-2 text-sm">
          <StepDot label="Upload" active={step === 'upload'} done={['preview', 'importing', 'done'].includes(step)} />
          <ArrowRight className="w-4 h-4 text-neutral-400" />
          <StepDot label="Preview & Resolve" active={step === 'preview'} done={['importing', 'done'].includes(step)} />
          <ArrowRight className="w-4 h-4 text-neutral-400" />
          <StepDot label="Confirm" active={step === 'importing' || step === 'done'} done={step === 'done'} />
        </div>

        <div className="flex-1 overflow-y-auto pr-2 -mr-2">
          {/* UPLOAD */}
          {step === 'upload' && (
            <div className="py-12 text-center" data-testid="upload-step">
              <div className="mx-auto w-16 h-16 rounded-full bg-emerald-50 flex items-center justify-center mb-4">
                <Upload className="w-8 h-8 text-emerald-600" />
              </div>
              <h3 className="text-lg font-semibold mb-2">Upload Zoho Invoice Details</h3>
              <p className="text-sm text-neutral-600 mb-6">
                Supports <code className="px-1 bg-neutral-100 rounded">.xlsx</code> and <code className="px-1 bg-neutral-100 rounded">.csv</code> exports from Zoho Books.
                <br />Expected columns: <code>invoice_number</code>, <code>date</code>, <code>customer_name</code>, <code>bcy_total</code>, <code>sgst</code>, <code>cgst</code>, <code>igst</code>, <code>tds_amount</code>, <code>gst_no</code>.
              </p>
              <label className="inline-flex items-center gap-2 px-6 py-3 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg cursor-pointer font-medium" data-testid="upload-input-label">
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                {loading ? 'Parsing…' : 'Choose File'}
                <input type="file" accept=".xlsx,.csv" className="hidden" onChange={onFileUpload} disabled={loading} data-testid="zoho-report-file-input" />
              </label>
              {file && <p className="mt-3 text-xs text-neutral-500">{file.name}</p>}
            </div>
          )}

          {/* PREVIEW */}
          {step === 'preview' && preview && (
            <div className="space-y-4" data-testid="preview-step">
              {/* Summary grid */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <StatCard icon={Receipt} label="Rows Parsed" value={s.total_rows_parsed} color="slate" />
                <StatCard icon={FileText} label="Unique Invoices" value={s.unique_invoices} color="blue" />
                <StatCard icon={CheckCircle2} label="Ready to Import" value={s.ready_to_import} color="emerald" />
                <StatCard icon={IndianRupee} label="Total Revenue" value={fmt(s.total_revenue)} color="violet" numeric={false} />
                <StatCard icon={TrendingUp} label="Total GST" value={fmt(s.total_gst)} color="amber" numeric={false} />
                <StatCard icon={ShieldAlert} label="Total TDS" value={fmt(s.total_tds)} color="orange" numeric={false} />
                <StatCard icon={Users} label="New Clients" value={s.new_clients_to_create} color="teal" />
                <StatCard icon={History} label="Existing Matched" value={s.existing_clients_matched} color="indigo" />
              </div>

              {/* Attention needed */}
              {(s.needs_state_resolution > 0 || s.duplicates_in_db > 0 || s.invalid > 0) && (
                <div className="rounded-lg border border-amber-200 bg-amber-50 p-3">
                  <div className="flex items-start gap-2">
                    <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                    <div className="text-sm">
                      <p className="font-semibold text-amber-900">Attention needed before confirming:</p>
                      <ul className="mt-1 space-y-0.5 text-amber-800 text-xs">
                        {s.needs_state_resolution > 0 && <li>• {s.needs_state_resolution} invoice(s) have no GSTIN — pick a state for each</li>}
                        {s.duplicates_in_db > 0 && <li>• {s.duplicates_in_db} invoice number(s) already exist — pick skip or overwrite</li>}
                        {s.invalid > 0 && <li>• {s.invalid} invoice(s) have validation errors — will be skipped</li>}
                      </ul>
                    </div>
                  </div>
                </div>
              )}

              {/* Date range + year breakdown */}
              {preview.year_breakdown?.length > 0 && (
                <div className="rounded-lg border bg-white p-3">
                  <p className="text-xs font-semibold text-neutral-600 mb-2">Year-wise breakdown</p>
                  <div className="flex flex-wrap gap-2">
                    {preview.year_breakdown.map((y) => (
                      <div key={y.year} className="px-3 py-1.5 rounded-md bg-slate-100 text-xs">
                        <span className="font-semibold">{y.year}</span>: {y.count} invoices · {fmt(y.total)}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Top clients */}
              {preview.top_clients?.length > 0 && (
                <Collapsible
                  title={`Top ${preview.top_clients.length} clients by revenue`}
                  open={showSection.ready}
                  onToggle={() => setShowSection((p) => ({ ...p, ready: !p.ready }))}
                  count={null}
                >
                  <div className="text-xs divide-y">
                    {preview.top_clients.map((c, i) => (
                      <div key={i} className="flex justify-between py-1.5">
                        <span className="truncate max-w-[70%]">{c.name}</span>
                        <span className="font-semibold">{fmt(c.total)}</span>
                      </div>
                    ))}
                  </div>
                </Collapsible>
              )}

              {/* Needs state resolution */}
              {preview.needs_state?.length > 0 && (
                <Collapsible
                  title="Invoices needing state resolution"
                  count={preview.needs_state.length}
                  tone="amber"
                  open={showSection.needs_state}
                  onToggle={() => setShowSection((p) => ({ ...p, needs_state: !p.needs_state }))}
                >
                  <div className="space-y-2 text-xs">
                    {preview.needs_state.map((r) => (
                      <div key={r.invoice_number} className="flex items-center gap-2 py-1 border-b border-neutral-100">
                        <span className="font-mono font-semibold w-28 truncate">{r.invoice_number}</span>
                        <span className="flex-1 truncate">{r.customer_name}</span>
                        <span className="text-neutral-500">{fmt(r.total)}</span>
                        <Select
                          value={stateResolutions[r.invoice_number] || ''}
                          onValueChange={(v) => setStateResolutions((p) => ({ ...p, [r.invoice_number]: v }))}
                        >
                          <SelectTrigger className="h-8 w-44" data-testid={`state-select-${r.invoice_number}`}>
                            <SelectValue placeholder="Pick state…" />
                          </SelectTrigger>
                          <SelectContent>
                            {(preview.indian_states || []).map((st) => (
                              <SelectItem key={st} value={st}>{st}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    ))}
                  </div>
                </Collapsible>
              )}

              {/* Duplicates */}
              {preview.duplicates?.length > 0 && (
                <Collapsible
                  title="Duplicate invoice numbers (already in DB)"
                  count={preview.duplicates.length}
                  tone="orange"
                  open={showSection.duplicates}
                  onToggle={() => setShowSection((p) => ({ ...p, duplicates: !p.duplicates }))}
                >
                  <div className="mb-2 flex gap-2">
                    <Button size="sm" variant="outline" onClick={() => {
                      const all = {}; preview.duplicates.forEach((d) => { all[d.invoice_number] = 'skip'; });
                      setDuplicateResolutions(all);
                    }} data-testid="skip-all-duplicates">Skip All</Button>
                    <Button size="sm" variant="outline" onClick={() => {
                      const all = {}; preview.duplicates.forEach((d) => { all[d.invoice_number] = 'overwrite'; });
                      setDuplicateResolutions(all);
                    }} data-testid="overwrite-all-duplicates">Overwrite All</Button>
                  </div>
                  <div className="space-y-1 text-xs">
                    {preview.duplicates.map((r) => (
                      <div key={r.invoice_number} className="flex items-center gap-2 py-1 border-b border-neutral-100">
                        <span className="font-mono font-semibold w-28 truncate">{r.invoice_number}</span>
                        <span className="flex-1 truncate">{r.customer_name}</span>
                        <span className="text-neutral-500">{fmt(r.total)}</span>
                        <Select
                          value={duplicateResolutions[r.invoice_number] || 'skip'}
                          onValueChange={(v) => setDuplicateResolutions((p) => ({ ...p, [r.invoice_number]: v }))}
                        >
                          <SelectTrigger className="h-8 w-32">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="skip">Skip</SelectItem>
                            <SelectItem value="overwrite">Overwrite</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    ))}
                  </div>
                </Collapsible>
              )}

              {/* Invalid */}
              {preview.invalid?.length > 0 && (
                <Collapsible
                  title="Invalid invoices (will be skipped)"
                  count={preview.invalid.length}
                  tone="red"
                  open={showSection.invalid}
                  onToggle={() => setShowSection((p) => ({ ...p, invalid: !p.invalid }))}
                >
                  <div className="space-y-1 text-xs">
                    {preview.invalid.map((r) => (
                      <div key={r.invoice_number} className="py-1 border-b border-neutral-100">
                        <div className="flex justify-between font-mono font-semibold">
                          <span>{r.invoice_number}</span>
                          <span>{r.customer_name}</span>
                        </div>
                        <ul className="mt-0.5 ml-2 text-red-700">
                          {(r.validation_issues || []).map((iss, i) => <li key={i}>• {iss}</li>)}
                        </ul>
                      </div>
                    ))}
                  </div>
                </Collapsible>
              )}

              {/* Import policy notice */}
              <div className="rounded-lg border bg-blue-50 border-blue-200 p-3 text-xs text-blue-900">
                <p className="font-semibold mb-1">Import policy applied:</p>
                <ul className="space-y-0.5">
                  <li>• All invoices imported as <strong>status = &quot;sent&quot;</strong> (user choice Q1b). You can manually mark paid later.</li>
                  <li>• Missing due dates default to <strong>invoice date</strong>.</li>
                  <li>• Unknown clients auto-created as <strong>archived</strong> with GSTIN + derived state + PAN.</li>
                  <li>• All invoices tagged as <strong>non_retainer</strong>; tag retainers manually.</li>
                  <li>• This batch can be fully rolled back via the History → Rollback button (triple-confirm).</li>
                </ul>
              </div>
            </div>
          )}

          {/* IMPORTING */}
          {step === 'importing' && (
            <div className="py-16 text-center" data-testid="importing-step">
              <Loader2 className="w-10 h-10 animate-spin text-emerald-600 mx-auto mb-4" />
              <p className="font-semibold">Importing invoices and creating clients…</p>
              <p className="text-xs text-neutral-500 mt-1">This may take a minute for large batches.</p>
            </div>
          )}

          {/* DONE */}
          {step === 'done' && result && (
            <div className="space-y-4" data-testid="done-step">
              <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-4 text-center">
                <CheckCircle2 className="w-12 h-12 text-emerald-600 mx-auto mb-2" />
                <h3 className="text-lg font-bold text-emerald-900">{result.message}</h3>
                <p className="text-xs text-emerald-700 mt-1">Batch ID: <code className="bg-white px-1 rounded">{result.batch_id}</code></p>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <StatCard icon={CheckCircle2} label="Imported" value={result.imported_count} color="emerald" />
                <StatCard icon={History} label="Overwritten" value={result.overwritten_count} color="orange" />
                <StatCard icon={Users} label="Clients Created" value={result.clients_created_count} color="blue" />
                <StatCard icon={X} label="Skipped" value={result.skipped_count} color="slate" />
              </div>
              {result.clients_created?.length > 0 && (
                <div className="text-xs border rounded-lg p-3 max-h-48 overflow-y-auto">
                  <p className="font-semibold mb-2">New archived clients created:</p>
                  {result.clients_created.map((c) => (
                    <div key={c.id} className="py-1 border-b border-neutral-100 flex justify-between">
                      <span>{c.name}</span>
                      <span className="text-neutral-500">{c.gstin || 'no GSTIN'}</span>
                    </div>
                  ))}
                </div>
              )}
              <p className="text-xs text-neutral-500 text-center">
                To undo this batch, go to Accounting → Imports → History and click &quot;Rollback&quot;.
              </p>
            </div>
          )}
        </div>

        <DialogFooter className="pt-3 border-t">
          {step === 'upload' && (
            <Button variant="outline" onClick={handleClose} data-testid="close-btn">Cancel</Button>
          )}
          {step === 'preview' && (
            <>
              <Button variant="outline" onClick={reset} data-testid="upload-different-file">Upload Different File</Button>
              <Button onClick={onConfirmImport} disabled={!canConfirm() || s.ready_to_import === 0 && !Object.values(duplicateResolutions).includes('overwrite') && Object.keys(stateResolutions).length === 0} data-testid="confirm-import-btn" className="bg-emerald-600 hover:bg-emerald-700">
                {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                Confirm & Import {s.ready_to_import + (Object.keys(stateResolutions).length) + Object.values(duplicateResolutions).filter((v) => v === 'overwrite').length} Invoices
              </Button>
            </>
          )}
          {step === 'done' && (
            <Button onClick={handleClose} data-testid="close-done">Close</Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ──────────────────────────────────────────────────────────────────
// Sub-components
// ──────────────────────────────────────────────────────────────────
function StepDot({ label, active, done }) {
  return (
    <div className={`flex items-center gap-1 px-2 py-1 rounded-full text-xs ${active ? 'bg-emerald-100 text-emerald-800 font-semibold' : done ? 'text-emerald-600' : 'text-neutral-500'}`}>
      {done ? <CheckCircle2 className="w-3.5 h-3.5" /> : <div className={`w-2 h-2 rounded-full ${active ? 'bg-emerald-600' : 'bg-neutral-300'}`} />}
      {label}
    </div>
  );
}

function StatCard({ icon: Icon, label, value, color = 'slate', numeric = true }) {
  const palette = {
    slate: 'bg-slate-50 text-slate-700 border-slate-200',
    blue: 'bg-blue-50 text-blue-700 border-blue-200',
    emerald: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    violet: 'bg-violet-50 text-violet-700 border-violet-200',
    amber: 'bg-amber-50 text-amber-700 border-amber-200',
    orange: 'bg-orange-50 text-orange-700 border-orange-200',
    teal: 'bg-teal-50 text-teal-700 border-teal-200',
    indigo: 'bg-indigo-50 text-indigo-700 border-indigo-200',
  };
  return (
    <div className={`rounded-lg border p-3 ${palette[color]}`}>
      <div className="flex items-center gap-1.5 text-xs opacity-80">
        <Icon className="w-3.5 h-3.5" />
        {label}
      </div>
      <p className="text-lg font-bold mt-1">{numeric ? formatNumber(value ?? 0) : value}</p>
    </div>
  );
}

function Collapsible({ title, count, tone = 'slate', open, onToggle, children }) {
  const toneMap = {
    slate: 'bg-white border-slate-200',
    amber: 'bg-amber-50/40 border-amber-200',
    orange: 'bg-orange-50/40 border-orange-200',
    red: 'bg-red-50/40 border-red-200',
  };
  return (
    <div className={`rounded-lg border ${toneMap[tone]}`}>
      <button onClick={onToggle} className="w-full flex items-center justify-between p-3 text-sm font-semibold">
        <span className="flex items-center gap-2">
          {title}
          {count != null && <Badge variant="outline" className="text-xs">{count}</Badge>}
        </span>
        {open ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
      </button>
      {open && <div className="px-3 pb-3">{children}</div>}
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────
// ROLLBACK MODAL (Triple-Confirm)
// ──────────────────────────────────────────────────────────────────
export function ZohoRollbackModal({ open, onClose, batchId, onDone }) {
  const [stage, setStage] = useState('init'); // init | step2 | step3 | done
  const [loading, setLoading] = useState(false);
  const [context, setContext] = useState(null);
  const [step1Token, setStep1Token] = useState('');
  const [step2Token, setStep2Token] = useState('');
  const [phrase2, setPhrase2] = useState('');
  const [phrase3, setPhrase3] = useState('');
  const [result, setResult] = useState(null);

  const reset = useCallback(() => {
    setStage('init'); setLoading(false); setContext(null);
    setStep1Token(''); setStep2Token(''); setPhrase2(''); setPhrase3(''); setResult(null);
  }, []);

  const close = () => { reset(); onClose(); };

  React.useEffect(() => {
    if (open && batchId) {
      (async () => {
        setLoading(true);
        try {
          const res = await adminApi.post('/api/admin/accounting/import-zoho-report/rollback/init', { batch_id: batchId });
          setContext(res.data);
          setStep1Token(res.data.step1_token);
          setStage('step2');
        } catch (err) {
          toast.error(err.response?.data?.detail || 'Failed to init rollback');
          onClose();
        } finally { setLoading(false); }
      })();
    }
  }, [open, batchId, onClose]);

  const submitStep2 = async () => {
    setLoading(true);
    try {
      const res = await adminApi.post('/api/admin/accounting/import-zoho-report/rollback/confirm-step2', {
        batch_id: batchId, step1_token: step1Token, phrase: phrase2,
      });
      setStep2Token(res.data.step2_token);
      setStage('step3');
    } catch (err) { toast.error(err.response?.data?.detail || 'Step 2 failed'); }
    finally { setLoading(false); }
  };

  const submitStep3 = async () => {
    setLoading(true);
    try {
      const res = await adminApi.post('/api/admin/accounting/import-zoho-report/rollback/execute', {
        batch_id: batchId, step1_token: step1Token, step2_token: step2Token, final_phrase: phrase3,
      });
      setResult(res.data);
      setStage('done');
      toast.success(res.data.message);
      if (onDone) onDone();
    } catch (err) { toast.error(err.response?.data?.detail || 'Rollback failed'); }
    finally { setLoading(false); }
  };

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent className="max-w-lg" data-testid="rollback-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-red-700">
            <ShieldAlert className="w-5 h-5" />
            Rollback Import Batch
          </DialogTitle>
        </DialogHeader>

        {stage === 'step2' && context && (
          <div className="space-y-3" data-testid="rollback-step2">
            <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm">
              <p className="font-semibold text-red-900 mb-1">Step 1 of 3 — Review impact</p>
              <ul className="text-xs text-red-800 space-y-0.5">
                <li>• Batch: <code className="bg-white px-1 rounded">{batchId}</code></li>
                <li>• Invoices to delete: <strong>{context.invoices_to_delete}</strong></li>
                <li>• Clients to delete: <strong>{context.clients_to_delete}</strong></li>
                <li>• Total value: <strong>{fmt(context.total_value)}</strong></li>
              </ul>
            </div>
            <div>
              <label className="text-xs font-semibold text-neutral-700">
                Step 2 of 3 — Type exactly: <code className="bg-neutral-100 px-1">I UNDERSTAND THIS IS PERMANENT</code>
              </label>
              <Input value={phrase2} onChange={(e) => setPhrase2(e.target.value)} placeholder="Type the phrase…" className="mt-1" data-testid="rollback-phrase-2" />
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={close}>Cancel</Button>
              <Button onClick={submitStep2} disabled={loading || phrase2 !== 'I UNDERSTAND THIS IS PERMANENT'} className="bg-red-600 hover:bg-red-700" data-testid="rollback-submit-2">
                Continue
              </Button>
            </DialogFooter>
          </div>
        )}

        {stage === 'step3' && (
          <div className="space-y-3" data-testid="rollback-step3">
            <div className="rounded-lg border border-red-300 bg-red-100 p-3 text-sm">
              <p className="font-semibold text-red-900">Step 3 of 3 — Final confirmation</p>
              <p className="text-xs text-red-800 mt-1">This cannot be undone. All imported invoices and auto-created clients in this batch will be permanently deleted.</p>
            </div>
            <div>
              <label className="text-xs font-semibold text-neutral-700">
                Type exactly: <code className="bg-neutral-100 px-1">DELETE BATCH {batchId}</code>
              </label>
              <Input value={phrase3} onChange={(e) => setPhrase3(e.target.value)} placeholder={`DELETE BATCH ${batchId}`} className="mt-1 font-mono text-xs" data-testid="rollback-phrase-3" />
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={close}>Cancel</Button>
              <Button onClick={submitStep3} disabled={loading || phrase3 !== `DELETE BATCH ${batchId}`} className="bg-red-600 hover:bg-red-700" data-testid="rollback-execute">
                {loading && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                ROLLBACK NOW
              </Button>
            </DialogFooter>
          </div>
        )}

        {stage === 'done' && result && (
          <div className="space-y-3" data-testid="rollback-done">
            <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-4 text-center">
              <CheckCircle2 className="w-10 h-10 text-emerald-600 mx-auto mb-2" />
              <p className="font-semibold text-emerald-900">{result.message}</p>
            </div>
            <DialogFooter>
              <Button onClick={close}>Close</Button>
            </DialogFooter>
          </div>
        )}

        {loading && stage === 'init' && (
          <div className="py-8 text-center">
            <Loader2 className="w-8 h-8 animate-spin text-neutral-500 mx-auto" />
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

// ──────────────────────────────────────────────────────────────────
// BATCH HISTORY LIST
// ──────────────────────────────────────────────────────────────────
export function ZohoImportHistory({ open, onClose, onRefresh }) {
  const [loading, setLoading] = useState(false);
  const [batches, setBatches] = useState([]);
  const [rollbackOpen, setRollbackOpen] = useState(null); // batch_id

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await adminApi.get('/api/admin/accounting/import-zoho-report/batches');
      setBatches(res.data.batches || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load batches');
    } finally { setLoading(false); }
  }, []);

  React.useEffect(() => { if (open) load(); }, [open, load]);

  return (
    <>
      <Dialog open={open} onOpenChange={onClose}>
        <DialogContent className="max-w-2xl" data-testid="zoho-import-history-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <History className="w-5 h-5" />
              Zoho Import History
            </DialogTitle>
          </DialogHeader>
          {loading ? (
            <div className="py-8 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></div>
          ) : batches.length === 0 ? (
            <div className="py-8 text-center text-sm text-neutral-500">No import batches yet</div>
          ) : (
            <div className="divide-y border rounded-lg max-h-[60vh] overflow-y-auto">
              {batches.map((b) => (
                <div key={b.batch_id} className="p-3 flex items-center justify-between text-sm" data-testid={`batch-row-${b.batch_id}`}>
                  <div className="min-w-0 flex-1">
                    <p className="font-mono text-xs truncate">{b.batch_id}</p>
                    <p className="text-xs text-neutral-500">{b.created_at} · {b.invoice_count} invoices · {fmt(b.total_value)}</p>
                  </div>
                  <Button size="sm" variant="outline" className="text-red-600 border-red-200 hover:bg-red-50" onClick={() => setRollbackOpen(b.batch_id)} data-testid={`rollback-btn-${b.batch_id}`}>
                    <ShieldAlert className="w-3.5 h-3.5 mr-1" /> Rollback
                  </Button>
                </div>
              ))}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={onClose}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {rollbackOpen && (
        <ZohoRollbackModal
          open={!!rollbackOpen}
          batchId={rollbackOpen}
          onClose={() => setRollbackOpen(null)}
          onDone={() => { setRollbackOpen(null); load(); if (onRefresh) onRefresh(); }}
        />
      )}
    </>
  );
}
